import { useQuery } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import {
  BarChart3,
  Building2,
  ClipboardCheck,
  FileText,
  FormInput,
  MapPin,
  Navigation,
  Search,
  Settings,
  Shield,
  UserRound,
} from "lucide-react";
import type { ComponentType } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { getNavigationSectionsForRole } from "@/constants/navigation";
import { formatPublicCode } from "@/lib/public-code";
import {
  rankSearchItems,
  type SearchItemKind,
  type SearchRankField,
} from "@/lib/search-ranking";
import { useUser } from "@/lib/user-context";
import { cn } from "@/lib/utils";
import { trpc } from "@/utils/trpc";

type SearchItem = {
  id: string;
  group: string;
  title: string;
  subtitle?: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  keywords?: string[];
  kind?: SearchItemKind;
  searchFields?: SearchRankField[];
};

export function AppSearchTrigger({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { systemRole, hasPermission } = useUser();
  const canSearchManagerData = hasPermission("site_manager");
  const dateRange = useMemo(() => {
    const end = new Date();
    const start = subDays(end, 30);
    return {
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
    };
  }, []);

  const managedSites = useQuery({
    ...trpc.getManagedSites.queryOptions(),
    enabled: canSearchManagerData,
  });
  const workers = useQuery({
    ...trpc.getWorkers.queryOptions(),
    enabled: canSearchManagerData,
  });
  const forms = useQuery({
    ...trpc.forms.listBySite.queryOptions({ limit: 100 }),
    enabled: canSearchManagerData,
  });
  const globalTemplates = useQuery({
    ...trpc.forms.listGlobalTemplates.queryOptions(),
    enabled: canSearchManagerData,
  });
  const report = useQuery({
    ...trpc.getManagerReport.queryOptions({
      ...dateRange,
      reportType: "attendance",
    }),
    enabled: canSearchManagerData,
  });

  const items = useMemo<SearchItem[]>(() => {
    const next: SearchItem[] = [];

    if (systemRole) {
      getNavigationSectionsForRole(systemRole).forEach((section) => {
        section.items.forEach((item) => {
          const title =
            typeof item.label === "string"
              ? item.label
              : (item.label[systemRole] ?? item.shortLabel);
          next.push({
            id: `nav:${item.to}`,
            group: "Pages",
            title,
            subtitle:
              typeof item.caption === "string" ? item.caption : undefined,
            href: item.to,
            icon: item.icon,
            kind: "page",
            keywords: [item.shortLabel, item.to],
          });
          item.subItems?.forEach((subItem) => {
            const subTitle =
              typeof subItem.label === "string"
                ? subItem.label
                : (subItem.label[systemRole] ?? "View");
            next.push({
              id: `nav:${subItem.to}`,
              group: "Pages",
              title: subTitle,
              subtitle:
                typeof subItem.caption === "string"
                  ? subItem.caption
                  : `Open ${title}`,
              href: subItem.to,
              icon: item.icon,
              kind: "page",
              keywords: [title, subItem.to],
            });
          });
        });
      });
    }

    next.push(
      {
        id: "action:settings",
        group: "Actions",
        title: "Account",
        subtitle: "Manage account and profile",
        href: "/account",
        icon: Settings,
        kind: "action",
      },
      {
        id: "action:new-site",
        group: "Actions",
        title: "Add site",
        subtitle: "Create a managed site",
        href: "/sites/new",
        icon: Building2,
        kind: "action",
      },
      {
        id: "action:reports",
        group: "Actions",
        title: "Export reports",
        subtitle: "Filter attendance and form exports",
        href: "/reports",
        icon: BarChart3,
        kind: "action",
      }
    );

    for (const site of managedSites.data ?? []) {
      const code = formatPublicCode(site.publicCode);
      next.push(
        {
          id: `site:${site.id}`,
          group: "Sites",
          title: site.name,
          subtitle: code ? `${code} · ${site.address}` : site.address,
          href: `/sites/${site.id}`,
          icon: Building2,
          kind: "site",
          keywords: [site.address, code ?? "", site.publicCode, site.status],
          searchFields: [
            { value: code, kind: "code" },
            { value: site.publicCode, kind: "code" },
            { value: site.address, kind: "subtitle" },
          ],
        },
        {
          id: `site-signins:${site.id}`,
          group: "Site actions",
          title: `${site.name} sign-ins`,
          subtitle: "Open site activity log",
          href: `/sites/${site.id}/signins`,
          icon: ClipboardCheck,
          kind: "site-action",
          keywords: [site.name, code ?? "", "attendance", "activity"],
          searchFields: [{ value: "sign-ins signins sign in", kind: "action" }],
        },
        {
          id: `site-entrances:${site.id}`,
          group: "Site actions",
          title: `${site.name} entrances`,
          subtitle: "Manage gates and QR entry",
          href: `/sites/${site.id}/entrances`,
          icon: MapPin,
          kind: "site-action",
          keywords: [site.name, code ?? "", "qr", "gate", "entrance"],
          searchFields: [
            { value: "entrances entrance gates gate qr", kind: "action" },
          ],
        },
        {
          id: `site-qr:${site.id}`,
          group: "Site actions",
          title: `${site.name} QR`,
          subtitle: "Open worker sign-in QR page",
          href: `/sites/${site.id}/qr`,
          icon: Navigation,
          kind: "site-action",
          keywords: [site.name, code ?? "", "qr", "sign in"],
          searchFields: [
            { value: "qr sign-in sign in signin", kind: "action" },
          ],
        }
      );
    }

    for (const worker of workers.data ?? []) {
      next.push({
        id: `worker:${worker.id}`,
        group: "Workers",
        title: worker.fullName ?? worker.email ?? "Unnamed worker",
        subtitle: [worker.jobTitle, worker.email].filter(Boolean).join(" · "),
        href: `/workers/${worker.id}`,
        icon: UserRound,
        kind: "worker",
        keywords: [
          worker.company ?? "",
          worker.jobTitle ?? "",
          worker.email ?? "",
        ],
        searchFields: [
          { value: worker.fullName, kind: "title" },
          { value: worker.email, kind: "email" },
          { value: getEmailLocalPart(worker.email), kind: "email" },
          { value: worker.jobTitle, kind: "subtitle" },
          { value: worker.company, kind: "keyword" },
        ],
      });
    }

    for (const item of forms.data ?? []) {
      const title = getFormTitle(item.form.data, item.template?.name);
      next.push({
        id: `form:${item.form.id}`,
        group: "Forms",
        title,
        subtitle: [
          item.site?.name,
          formatStatusLabel(item.form.status),
          item.submittedByName ?? item.submittedByEmail,
        ]
          .filter(Boolean)
          .join(" · "),
        href: `/permits/${item.form.id}`,
        icon: FileText,
        kind: "form",
        keywords: [
          item.template?.description ?? "",
          item.site?.name ?? "",
          item.submittedByEmail ?? "",
          item.submittedByName ?? "",
        ],
      });
    }

    for (const template of globalTemplates.data ?? []) {
      next.push({
        id: `global-template:${template.id}`,
        group: "Templates",
        title: template.name,
        subtitle: template.description ?? "Global form template",
        href: `/permits/templates/${template.id}`,
        icon: FormInput,
        kind: "template",
        keywords: [template.formType, template.description ?? ""],
      });
    }

    for (const signIn of report.data?.recentSignIns ?? []) {
      next.push({
        id: `signin:${signIn.signInId}`,
        group: "Recent sign-ins",
        title: signIn.workerName ?? "Worker sign-in",
        subtitle: [
          signIn.siteName,
          signIn.jobTitle,
          signIn.flagged ? "Flagged" : "",
        ]
          .filter(Boolean)
          .join(" · "),
        href: `/signins/${signIn.signInId}`,
        icon: ClipboardCheck,
        kind: "recent-sign-in",
        keywords: [signIn.siteName, signIn.jobTitle ?? ""],
      });
    }

    if (systemRole === "admin") {
      next.push(
        {
          id: "admin:sites",
          group: "Admin",
          title: "Admin sites overview",
          subtitle: "Review all sites and platform activity",
          href: "/admin/sites",
          icon: Shield,
          kind: "admin",
        },
        {
          id: "admin:users",
          group: "Admin",
          title: "User management",
          subtitle: "Review accounts and roles",
          href: "/admin/users",
          icon: Shield,
          kind: "admin",
        }
      );
    }

    return next;
  }, [
    forms.data,
    globalTemplates.data,
    managedSites.data,
    report.data,
    systemRole,
    workers.data,
  ]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const openItem = (href: string) => {
    setOpen(false);
    navigate(href);
  };

  const rankedItems = useMemo(
    () => rankSearchItems(items, search),
    [items, search]
  );

  return (
    <>
      <button
        type="button"
        className={cn(
          "hidden h-10 w-full max-w-[26rem] items-center gap-3 rounded-lg border border-border/60 bg-background/85 px-4 text-left text-sm text-muted-foreground shadow-[var(--surface-shadow-sm)] backdrop-blur transition-colors hover:bg-muted/60 md:flex",
          className
        )}
        onClick={() => setOpen(true)}
      >
        <Search className="size-4 text-foreground" />
        <span className="min-w-0 flex-1 truncate">
          Search sites, workers, forms...
        </span>
        <kbd className="hidden rounded-xs border border-border bg-background px-1.5 py-0.5 text-xs font-semibold text-muted-foreground lg:inline-flex">
          Cmd K
        </kbd>
      </button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Search OS04"
        description="Search pages, sites, workers, forms and recent sign-ins."
        className="max-w-[calc(100%-2rem)] sm:max-w-2xl"
        commandProps={{ shouldFilter: false }}
      >
        <CommandInput
          placeholder="Search sites, workers, forms..."
          value={search}
          onValueChange={setSearch}
        />
        <CommandList className="max-h-[28rem]">
          {rankedItems.length === 0 ? (
            <CommandEmpty>No results found.</CommandEmpty>
          ) : null}
          {groupedItems(rankedItems).map(([group, groupItems], index) => (
            <CommandGroup key={group} heading={group}>
              {index > 0 ? <CommandSeparator /> : null}
              {groupItems.map((item) => (
                <CommandItem
                  key={item.id}
                  value={[item.title, item.subtitle, ...(item.keywords ?? [])]
                    .filter(Boolean)
                    .join(" ")}
                  onSelect={() => openItem(item.href)}
                >
                  <item.icon className="size-4" />
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      <HighlightedText text={item.title} query={search} />
                    </div>
                    {item.subtitle ? (
                      <div className="truncate text-xs text-muted-foreground">
                        <HighlightedText text={item.subtitle} query={search} />
                      </div>
                    ) : null}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const tokens = query
    .trim()
    .split(/\s+/)
    .filter((token) => token.length > 0);

  if (!tokens.length) return text;

  const matcher = new RegExp(`(${tokens.map(escapeRegExp).join("|")})`, "ig");
  const parts = text.split(matcher);
  const normalizedTokens = tokens.map((token) => token.toLowerCase());

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = normalizedTokens.includes(part.toLowerCase());

        return isMatch ? (
          <mark
            // biome-ignore lint/suspicious/noArrayIndexKey: split output has no stable ids
            key={`${part}-${index}`}
            className="rounded-sm bg-primary/35 px-0.5 text-foreground"
          >
            {part}
          </mark>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: split output has no stable ids
          <span key={`${part}-${index}`}>{part}</span>
        );
      })}
    </>
  );
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function groupedItems(items: SearchItem[]) {
  const groups = new Map<string, SearchItem[]>();
  for (const item of items) {
    const groupItems = groups.get(item.group) ?? [];
    groupItems.push(item);
    groups.set(item.group, groupItems);
  }
  return Array.from(groups.entries());
}

function getEmailLocalPart(email?: string | null) {
  return email?.split("@")[0] ?? "";
}

function getFormTitle(data: unknown, templateName?: string | null) {
  if (templateName) return templateName;
  if (typeof data === "object" && data !== null && "meta" in data) {
    const meta = data.meta;
    if (
      typeof meta === "object" &&
      meta !== null &&
      "title" in meta &&
      typeof meta.title === "string"
    ) {
      return meta.title;
    }
  }
  return "Untitled form";
}

function formatStatusLabel(status: string) {
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
